import { StorefrontConfigResponse, FONT_STACKS, FontStackKey } from "config-types";
import logoSVG from '../svg/logo.svg';


export const setFavicon = (url:string): void => {
    const faviconUrl = url;
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="shortcut icon"]');
  
    if (favicon) {
      favicon.href = faviconUrl;
    } else {
      const newFavicon = document.createElement('link');
      newFavicon.rel = 'shortcut icon';
      newFavicon.href = faviconUrl;
      document.head.appendChild(newFavicon);
    }
  };

  // Vite resolves the .svg import to a URL string, so the return type is
  // always string (the old `string | SVGElement` union forced callers to
  // .toString() defensively).
  export const getLogoUrl = (): string => {
    const shopConfigJSON = localStorage.getItem('shopConfig');
    const shopConfig: StorefrontConfigResponse | null = shopConfigJSON
      ? JSON.parse(shopConfigJSON)
      : null;
  
    const logoUrl = shopConfig && shopConfig?.theme?.logoUrl
      ? shopConfig.theme.logoUrl
      : logoSVG;

      return logoUrl;

  }

  
// --- Runtime semantic-token theming -----------------------------------------
// The design system reads HSL-channel tokens (--primary, --secondary, ...)
// from :root (see index.css). Shop theme colors override those channels at
// boot so bg-primary / text-primary-foreground etc. restyle live. The legacy
// --app-primary-* shade scale (tailwindcssPaletteGenerator) is still written
// separately for the few remaining numeric-shade utilities.

import { hexToHSL } from './tailwind';

export type SemanticThemeInput = {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  backgroundColor?: string | null;
  /** CSS length, e.g. "8px". Written to --radius verbatim. */
  radius?: string | null;
  /** FONT_STACKS key; unknown keys are ignored (default stack stays). */
  fontFamily?: string | null;
};

const toTriplet = ({ h, s, l }: { h: number; s: number; l: number }) => `${h} ${s}% ${l}%`;

/**
 * White text on dark brand colors, near-black on light ones — decided by WCAG
 * relative luminance rather than HSL lightness (a yellow at 53% HSL lightness
 * is visually light and needs dark text).
 */
const foregroundFor = (hex: string) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return '0 0% 100%';
  const channel = (c: string) => {
    const v = parseInt(c, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * channel(m[1]) + 0.7152 * channel(m[2]) + 0.0722 * channel(m[3]);
  return luminance > 0.4 ? '240 10% 8%' : '0 0% 100%';
};

export const applySemanticThemeTokens = (
  theme: SemanticThemeInput | null | undefined,
  root: HTMLElement = document.documentElement,
): void => {
  if (!theme) return;
  if (theme.primaryColor) {
    const hsl = hexToHSL(theme.primaryColor);
    root.style.setProperty('--primary', toTriplet(hsl));
    root.style.setProperty('--ring', toTriplet(hsl));
    root.style.setProperty('--primary-foreground', foregroundFor(theme.primaryColor));
  }
  if (theme.secondaryColor) {
    const hsl = hexToHSL(theme.secondaryColor);
    root.style.setProperty('--secondary', toTriplet(hsl));
    root.style.setProperty('--secondary-foreground', foregroundFor(theme.secondaryColor));
  }
  if (theme.backgroundColor) {
    const hsl = hexToHSL(theme.backgroundColor);
    root.style.setProperty('--background', toTriplet(hsl));
    root.style.setProperty('--foreground', foregroundFor(theme.backgroundColor));
  }
  if (theme.radius) {
    root.style.setProperty('--radius', theme.radius);
  }
  if (theme.fontFamily && theme.fontFamily in FONT_STACKS) {
    root.style.setProperty('--font-sans', FONT_STACKS[theme.fontFamily as FontStackKey]);
  }
};
